import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import {
	DialogTitle,
	DialogActions,
	Dialog,
	useMediaQuery,
	Box,
	CircularProgress
} from '@mui/material';
import {jsPDF} from 'jspdf';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLottie } from 'lottie-react';
import PropTypes from 'prop-types';
import animationData from '../../../Assets/Images/Loader.json';
import permissions from '../../../utils/permissions.js';
import {
	downloadTemplate,
	deleteTemplateById
} from '../../../Services/api-config.js';
import DownloadIcon from '../../../Assets/Images/svgs/download.svg';
import { downloadBase64File, getErrorMessage } from '../../../utils/util.js';
import PrimaryButton from '../../Common/Button/primary-button.js';
import ApproveRejectDialog from '../../Common/Dashboard/approve-reject-dialog.js';
import customToast from '../../Common/custom-toast.js';
import CKTemplatePopup from '../CKEditorComponent/CKEditorpopup/cktemplate-popup.js';
import { ReactComponent as CrossIcon } from '../../../Assets/Images/close-circle-grey.svg';
import './pdfpreview-new.css';

const PreviewUploadDocument = ({
	thumbnailContent,
	openPDF,
	setOpenPDF,
	thumbFlag,
	setThumbFlag,
	removeMarginLeftRight,
	onFetchAPI,
	selectedFileStatus,
	isLoading,
	recipientPage = false
}) => {
	const [documentLoading, setDocumentLoading] = useState(
		!(
			selectedFileStatus?.type?.includes('png')
			|| selectedFileStatus?.type?.includes('jpg')
			|| selectedFileStatus?.type?.includes('jpeg')
		)
	);
	const { showToast } = customToast();
	const navigate = useNavigate();
	const location = useLocation();
	const [pdfFile, setPdfFile] = useState(null);
	const [numPages, setNumPages] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [deleteTemplate, setDeleteTemplate] = useState(false);
	const { TemplateEngineEdit, TemplateEngineDelete } = permissions();
	const isMobile = useMediaQuery('(max-width: 600px)');
	const pdfContainerRef = useRef();
	const options = {
		animationData,
		loop: true,
		autoplay: true
	};
	const { View } = useLottie(options);

	const checkValid = (thumbFlag, thumbnailContent) =>
		(thumbFlag && thumbnailContent?.templateHtml)
		|| thumbnailContent?.templateHtml === '';

	useEffect(() => {
		if (checkValid(thumbFlag, thumbnailContent)) {
			const doc = jsPDF();
			const marginData = {
				left: 5,
				right: 5,
				bottom: 5,
				top: 5
			};

			const createTempdiv = window.document.createElement('div');

			// Showing margin in preview
			const savedmargin = thumbnailContent?.templateConfig?.marginData;
			if (savedmargin) {
				const parseMargn = JSON.parse(
					thumbnailContent?.templateConfig?.marginData
				);

				if (parseMargn) {
					marginData.left = parseMargn.left;
					marginData.right = parseMargn.right;
					marginData.top = Math.max(parseMargn.top, 5);
					marginData.bottom = Math.max(parseMargn.bottom, 5);
					if (!removeMarginLeftRight) {
						createTempdiv.style.marginLeft = parseMargn.left + 'px';
						createTempdiv.style.marginRight = parseMargn.right + 'px';
					}
				}
			}

			createTempdiv.innerHTML = thumbnailContent?.templateHtml;

			const checkEditorTag = createTempdiv.querySelector('#editor');

			const modHtml = createTempdiv.innerHTML.replaceAll('.', ' .');
			createTempdiv.innerHTML = modHtml;

			if (!checkEditorTag) {
				createTempdiv.id = 'editor';
				createTempdiv.classList.add(
					'ck-restricted-editing_mode_standard',
					'ck-blurred',
					'ck',
					'ck-content',
					'ck-editor__editable',
					'ck-editor__editable_inline'
				);

				createTempdiv.setAttribute('role', 'textbox');
				createTempdiv.setAttribute('dir', 'ltr');
				createTempdiv.setAttribute('contenteditable', 'true');
			}

			doc.html(createTempdiv, {
				callback(generatedPdf) {
					const checkBorder = thumbnailContent?.templateConfig?.borderData;
					/// adding border for each page
					if (checkBorder) {
						const parseBorder = JSON.parse(checkBorder);
						if (parseBorder?.border) {
							// For adding border to each page
							for (let i = 0; i < doc.getNumberOfPages(); i++) {
								doc.setPage(i + 1);
								doc.setDrawColor(0, 0, 0);
								doc.setLineWidth(4);

								if (parseBorder.border > 4) {
									doc.setLineWidth(2);
								} else {
									doc.setLineWidth(parseBorder.border);
								}

								doc.rect(
									5,
									5,
									doc.internal.pageSize.width - 10,
									doc.internal.pageSize.height - 10,
									'S'
								);
							}
						}
					}

					const blob = generatedPdf.output('blob');
					const pdfBlob = new File([blob], 'thumbnail.pdf', {
						type: 'application/pdf'
					});

					setPdfFile(pdfBlob);
				},
				margin: [
					Number.parseInt(marginData.top, 10),
					5,
					Number.parseInt(marginData.bottom, 10),
					5
				],
				autoPaging: 'text',
				x: 0,
				y: 0,
				width: 190,
				windowWidth: 675
			});
		}
	}, [thumbnailContent]);

	const handleClosePDF = () => {
		setOpenPDF(false);
		if (thumbFlag) {
			setThumbFlag(thumbFlag);
		}

		setDocumentLoading(true);
		if (location.pathname === '/template') {
			setPdfFile(null);
		}
	};

	const onDocumentLoadSuccess = ({ numPages }) => {
		setCurrentPage(1);
		setNumPages(numPages);
	};

	const handleFileEdit = () => {
		navigate(`/edit?createmode=${true}&fileId=${thumbnailContent.templateId}`);
	};

	const handleCloseTemplate = () => {
		setIsPopupOpen(!isPopupOpen);
	};

	const handleScroll = () => {
		if (pdfContainerRef.current) {
			const scrollTop = pdfContainerRef.current.scrollTop;
			const pageHeight = pdfContainerRef.current.scrollHeight / numPages;
			const newPage = Math.floor(scrollTop / pageHeight) + 1;
			setCurrentPage(newPage);
		}
	};

	const handleDeleteAction = async () => {
		try {
			const deletedTemplateById = selectedFileStatus.templateId;
			const response = await deleteTemplateById(deletedTemplateById);
			if (response.status === 200) {
				onFetchAPI(deletedTemplateById);
				setOpenPDF(false);
				setDeleteTemplate(false);
				showToast('success', 'Template successfully deleted');
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		}
	};

	const handleDownloadModal = async (type, { templateId, templateName }) => {
		try {
			const response = await downloadTemplate(templateId, type);
			if (response?.status === 200) {
				downloadBase64File(
					response?.base64Data || response.data,
					templateName + `.${type}`,
					type,
					'application'
				);
				showToast('success', response?.message || '');
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
			return error;
		}
	};

	function getSelectedFile(thumbFlag, pdfFile, selectedFileStatus) {
		return thumbFlag ? pdfFile : selectedFileStatus;
	}

	function isImageFile(file) {
		const imageTypes = ['png', 'jpg', 'jpeg'];
		return file?.type && imageTypes.some((type) => file.type.includes(type));
	}

	function isTemplatePage(thumbFlag) {
		return thumbFlag && location.pathname === '/template';
	}

	function isLastPage(index, numPages) {
		return index < numPages - 1;
	}

	function getPadding(isMobile, isLoading) {
		if (isMobile) {
			return '16px';
		}

		if (isLoading) {
			return '0';
		}

		return '16px 25px';
	}

	function getMargin(isMobile) {
		return isMobile ? '14px' : 'initial';
	}

	return (
		<>
			<Dialog
				open={openPDF}
				onClose={handleClosePDF}
				maxWidth={false}
				slotProps={{
					paper: {
						style: {
							maxWidth: '600px',
							borderRadius: '10px',
							margin: getMargin(isMobile)
						}
					}
				}}
			>
				<Box
					sx={{
						padding: getPadding(isMobile, isLoading),
						backgroundColor: '#000',
						color: '#fff'
					}}
				>
					<style>
						{`
              /* Hide scrollbar for webkit-based browsers (Chrome, Safari) */
              ::-webkit-scrollbar {
                display: none;
              }
              /* Hide scrollbar for Firefox */
              
            `}
					</style>
					<DialogTitle
						style={{
							fontWeight: 400,
							fontSize: '12px',
							color: '#8E8E8E',
							padding: '10px 0',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: '10px',
							flexWrap: 'wrap'
						}}
					>
						<div style={{ minWidth: '100px' }}>
							{selectedFileStatus?.templateName || selectedFileStatus?.name}
						</div>

						{recipientPage && numPages > 0 && (
							<div style={{ minWidth: '60px', textAlign: 'center' }}>
								<p style={{ margin: 0 }}>
									{currentPage}/{numPages}
								</p>
							</div>
						)}

						{recipientPage && (
							<Box
								sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: '30px' }}
								onClick={handleClosePDF}
							>
								<CrossIcon
									style={{
										stroke: 'white',
										cursor: 'pointer',
										width: '24px',
										height: '24px'
									}}
								/>
							</Box>
						)}

						{selectedFileStatus?.showDownloadOptions && (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '10px',
									flexWrap: 'wrap'
								}}
							>
								<span style={{ color: 'white', fontWeight: 'bold' }}>CSV</span>
								<Box
									component='img'
									src={DownloadIcon}
									alt='Download CSV'
									style={{ cursor: 'pointer', width: '50px', height: '40px' }}
									onClick={() => handleDownloadModal('csv', selectedFileStatus)}
								/>
								<span style={{ color: 'white', fontWeight: 'bold' }}>XLSX</span>
								<Box
									component='img'
									src={DownloadIcon}
									alt='Download XLSX'
									style={{ cursor: 'pointer', width: '50px', height: '40px' }}
									onClick={() => handleDownloadModal('xlsx', selectedFileStatus)}
								/>
							</div>
						)}
					</DialogTitle>

					<div
						className='pdfpreview-container'
						style={{
							display: 'flex',
							justifyContent: 'center',
							height: '100%'
						}}
					>
						{getSelectedFile(thumbFlag, pdfFile, selectedFileStatus) && (
							<div
								style={{
									maxHeight: isMobile ? '73vh' : '75vh',
									overflowX: 'hidden',
									position: 'relative',
									borderRadius: '8px',
									width: '100%',
									height: '100%',
									display: 'flex',
									overflowY: documentLoading ? 'hidden' : 'auto'
								}}
								ref={pdfContainerRef}
								onScroll={handleScroll}
							>
								<div
									className='pdf-page'
									style={{
										width: '100%',
										height: documentLoading ? '75vh' : 'auto',
										display: 'flex',
										justifyContent: 'center'
									}}
								>
									{isImageFile(selectedFileStatus) ? (
										<img
											className='image-preview'
											src={selectedFileStatus.dataUrl}
											alt='error'
										/>
									) : (
										<Box width='100%' height='100%'>
											{documentLoading && (
												<Box
													sx={{
														minWidth: '600px',
														minHeight: '100%',
														maxWidth: '600px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center'
													}}
												>
													<div style={{ textAlign: 'center' }}>
														<CircularProgress />
														<p>loading document...</p>
													</div>
												</Box>
											)}

											<Box
												sx={{
													visibility: documentLoading ? 'hidden' : 'visible'
												}}
											>
												<Document
													file={
														thumbFlag ? pdfFile : selectedFileStatus.dataUrl
													}
													onLoadSuccess={onDocumentLoadSuccess}
													loading={View}
												>
													{Array.from({ length: numPages }, (_, index) => (
														<div key={index}>
															<Page
																pageNumber={index + 1}
																renderTextLayer={false}
																renderAnnotationLayer={false}
																onRenderSuccess={() => {
																	if (index === numPages - 1) {
																		setDocumentLoading(false);
																	}
																}}
															/>
															{isLastPage(index, numPages) && (
																<hr
																	style={{
																		borderTop: '1px solid #000',
																		margin: '20px 0'
																	}}
																/>
															)}
														</div>
													))}
												</Document>
											</Box>
										</Box>
									)}
								</div>
							</div>
						)}
					</div>
					{!recipientPage && !documentLoading && (
						<DialogActions
							style={{
								padding: '10px 0',
								display: 'flex',
								flexDirection: isMobile ? 'column' : 'row',
								justifyContent: isMobile ? 'intial' : 'space-between',
								alignItems: isMobile ? 'flex-start' : 'center',
								gap: isMobile ? '5px 0' : '0'
							}}
						>
							{numPages > 0 && (
								<div style={{ flex: 1 }}>
									<p>
										{currentPage}/{numPages}
									</p>
								</div>
							)}
							<Box
								sx={{
									display: 'flex',
									gap: '0 5px',
									justifyContent: isTemplatePage(thumbFlag)
										? 'inherit'
										: 'flex-end',
									marginLeft: '0px !important',
									width: isMobile ? '100%' : 'auto'
								}}
							>
								{isTemplatePage(thumbFlag) ? (
									<>
										<PrimaryButton
											width={'116px'}
											height={'40px'}
											value={'Cancel'}
											backgroundColor={'transparent'}
											borderRadius={'8px'}
											fontWeight={400}
											btnClass='template-preview_btn medium5'
											textcolor={'#B7D423'}
											border='2px solid #B7D423'
											onClick={handleClosePDF}
										/>
										{TemplateEngineEdit && (
											<PrimaryButton
												width={'140px'}
												height={'40px'}
												value={'Copy Template'}
												backgroundColor={'#B7D423'}
												borderRadius={'8px'}
												fontWeight={400}
												btnClass='template-preview_btn medium5'
												textcolor={'#21241E'}
												onClick={() => setIsPopupOpen(true)}
												customStyle={{
													lineHeight: isMobile ? '1.2' : 'inherit'
												}}
											/>
										)}
										{TemplateEngineEdit && (
											<PrimaryButton
												width={'116px'}
												height={'40px'}
												value={'Edit'}
												backgroundColor={'#B7D423'}
												borderRadius={'8px'}
												fontWeight={400}
												btnClass='template-preview_btn medium5'
												textcolor={'#21241E'}
												onClick={handleFileEdit}
											/>
										)}
										{TemplateEngineDelete && (
											<PrimaryButton
												value={'Delete'}
												onClick={() => setDeleteTemplate(true)}
												textcolor='#E96666'
												padding='10px 16px'
												width='97px'
												height='40px'
												backgroundColor='transparent'
												borderRadius='8px'
												border='2px solid #E96666'
											/>
										)}
									</>
								) : (
									<PrimaryButton
										width={'116px'}
										height={'40px'}
										value={'Close'}
										backgroundColor={'#B7D423'}
										borderRadius={'8px'}
										fontWeight={400}
										textcolor={'#21241E'}
										onClick={handleClosePDF}
									/>
								)}
							</Box>
						</DialogActions>
					)}
				</Box>
			</Dialog>

			<ApproveRejectDialog
				open={deleteTemplate}
				onClose={() => {
					setDocumentLoading(true);
					setDeleteTemplate(false);
				}}
				DialogTitles={'Delete Template'}
				DialogContents={'Are you sure you want to delete the template?'}
				handleCancelButton={'No, Cancel'}
				handleActionButton={'Yes, Delete'}
				handleActionFunction={handleDeleteAction}
				width={'100px'}
			/>

			<CKTemplatePopup
				open={isPopupOpen}
				onClose={() => {
					setDocumentLoading(true);
					handleCloseTemplate();
				}}
				type='create'
			/>
		</>
	);
};

export default PreviewUploadDocument;

PreviewUploadDocument.propTypes = {
	thumbnailContent: PropTypes.any,
	openPDF: PropTypes.any,
	setOpenPDF: PropTypes.any,
	thumbFlag: PropTypes.any,
	setThumbFlag: PropTypes.any,
	removeMarginLeftRight: PropTypes.any,
	onFetchAPI: PropTypes.any,
	selectedFileStatus: PropTypes.any,
	isLoading: PropTypes.any
};
